import { useEffect, useRef, useState, KeyboardEvent, MouseEvent } from 'react';

import { ChevronRight, ExpandMore, Menu as MenuIcon } from '@mui/icons-material';
import { getQueryClient } from '~/services/query-client';
import {
  Box,
  ClickAwayListener,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Popper,
  SwipeableDrawer,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import { Key as KeyIcon, CreditCard as CardIcon, UserPlus as UserIcon } from 'react-feather';
import { QAMenu, MenuItemDef, filterMenuByRole, menu } from './menu';
import { makeStyles } from '@mui/styles';
import routes, { getRoutePath } from '~/router/routes';
import { googleLogout } from '@react-oauth/google';
import { useManageChat } from '~/modules/@shared/hooks/useManageChat';
import { GAEventPath, GASendEvent } from '~/analytics';
import { getConfig } from '~/services/config';
import { useRoleAuthenticationContext } from '~/contexts/role-authentication';
import { useLocationContext } from '~/contexts/location.context.tsx';
import { useGetQSReportListQuery } from '~/services/reports.ts';
import useModalTemplateStore from '~/modules/@shared/components/ModalTemplate/hooks/useModalTemplateStore';
import useTakePaymentStore from '~/modules/payment/components/TakePayment/hooks/useTakePaymentStore';
import { useGetIds } from '~/modules/auth';

const useStyles = makeStyles(() => {
  const color = '#ffffff';
  return {
    arrow: {
      position: 'absolute',
      top: '5px',
      width: '1em',
      height: '0.71em',
      boxSizing: 'border-box',
      color,
      '&::before': {
        content: '""',
        margin: 'auto',
        display: 'block',
        width: '100%',
        height: '100%',
        backgroundColor: 'currentColor',
        transform: 'rotate(135deg)',
      },
    },
  };
});

export default function Navbar({ quickActions = false }) {
  const { checkRoles } = useRoleAuthenticationContext();
  const { locationId, isDea } = useLocationContext();
  const { data: reportQSList } = useGetQSReportListQuery({ locationId });

  const reportQSMenuItems = (reportQSList || [])
    .filter((report) => report.active)
    .map<MenuItemDef>((report) => {
      return {
        id: report.report_id.toString(),
        title: report.name,
        link: `/${routes.reports.quicksight.root}`.replace(':id', report.report_id.toString()),
      };
    });

  const quickActionsMenu: MenuItemDef[] = filterMenuByRole(QAMenu, checkRoles);
  const mainMenu: MenuItemDef[] = filterMenuByRole(menu, checkRoles);

  const reportMenu = mainMenu.find((item) => item.id === 'reports');
  reportMenu?.subItems?.push(...reportQSMenuItems);
  reportMenu?.subItems?.sort((a, b) => a.title.localeCompare(b.title));

  return (
    <Box>
      <DesktopNavbar {...{ menu: quickActions ? quickActionsMenu : mainMenu, quickActions }} isDea={isDea} />
      <MobileNavbar menu={[...mainMenu, ...quickActionsMenu]} {...{ quickActions }} isDea={isDea} />
    </Box>
  );
}

function DesktopNavbar({ menu, quickActions, isDea }: { menu: MenuItemDef[]; quickActions: boolean; isDea: boolean }) {
  return (
    <List
      component="nav"
      sx={{
        display: {
          xs: 'none',
          sm: 'none',
          md: 'inline-flex',
        },
        backgroundColor: quickActions ? 'white' : 'inherit',
        color: quickActions ? 'black' : 'inherit',
        borderRadius: '5px',
        padding: '0',
      }}>
      {menu.map((item) => (
        <DesktopMenuItem key={item.id} item={item} isDea={isDea} />
      ))}
    </List>
  );
}

function DesktopMenuItem({
  basePath = '',
  item,
  onClose,
  isDea,
}: {
  basePath?: string;
  item: MenuItemDef;
  onClose?: () => void;
  isDea: boolean;
}) {
  const ref = useRef(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const isTopLevel = !basePath;
  const navigate = useNavigate();
  const { memberId } = useGetIds();
  const openCreateInvoiceModal = useModalTemplateStore((state) => state.open);
  const openTakeAPayment = useTakePaymentStore((state) => state.open);
  const setContactId = useTakePaymentStore((state) => state.setContactId);

  const classes = useStyles();
  const [arrowRef, setArrowRef] = useState(null);
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleMenuItemClick = (item: MenuItemDef) => {
    if (item.subItems) {
      setAnchorEl(ref.current);
    } else {
      setAnchorEl(null);
      onClose?.();
      if (item.link) {
        navigate(basePath + item.link);
      } else if (item.onClick) {
        if (isDea) {
          item.onClick(() => openCreateInvoiceModal('createInvoice'));
        } else {
          setContactId(memberId);
          item.onClick(() => openTakeAPayment());
        }
      }
    }
  };

  const renderMenuLabel = (item: any) => {
    let prefixIcon = <></>;
    if (item.id === 'checkins') {
      prefixIcon = (
        <KeyIcon
          style={{
            paddingRight: '8px',
            color: '#7F869E',
          }}
        />
      );
    }
    if (item.id === 'take_payment') {
      prefixIcon = (
        <CardIcon
          style={{
            paddingRight: '8px',
            color: '#7F869E',
          }}
        />
      );
    }
    if (item.id === 'add_contact') {
      prefixIcon = (
        <UserIcon
          style={{
            paddingRight: '8px',
            color: '#7F869E',
          }}
        />
      );
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {prefixIcon}
        {item.title}
      </div>
    );
  };

  return (
    <>
      <ListItemButton
        ref={ref}
        sx={{ backgroundColor: isOpen ? 'rgba(0, 0, 0, 0.04)' : 'inherit', borderRadius: '5px' }}
        onClick={() => handleMenuItemClick(item)}>
        <ListItemText primary={renderMenuLabel(item)} />
        {item.subItems &&
          (isTopLevel ? (
            <ExpandMore
              style={{
                color: '#A6A6A6',
              }}
            />
          ) : (
            <ChevronRight />
          ))}
      </ListItemButton>
      {item.subItems && item.subItems.length > 0 && (
        <Popper
          anchorEl={anchorEl}
          open={isOpen}
          placement={basePath ? 'right-start' : 'bottom-start'}
          sx={{ zIndex: 9999 }}
          modifiers={[
            {
              name: 'arrow',
              enabled: true,
              options: {
                element: arrowRef,
              },
            },
          ]}>
          {
            // @ts-ignore:
            <span className={classes.arrow} ref={setArrowRef} />
          }
          <ClickAwayListener onClickAway={handleClose}>
            <Paper sx={{ minWidth: '200px', mt: isTopLevel ? 1 : 0 }}>
              <List component="nav" sx={{ width: '100%' }}>
                {item.subItems.map((subItem) => (
                  <DesktopMenuItem
                    key={subItem.id}
                    basePath={basePath + item.link}
                    item={subItem}
                    onClose={handleClose}
                    isDea={isDea}
                  />
                ))}
              </List>
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </>
  );
}

function MobileNavbar({ menu, quickActions, isDea }: { menu: MenuItemDef[]; quickActions: boolean; isDea: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const [openedMenu, setOpenedMenu] = useState<string | null>(null);
  const navigate = useNavigate();
  const { tryChat: tryCloseChat } = useManageChat({ close: true, disabled: true });
  const { clearToken } = getConfig();

  const toggleDrawer = (open: boolean) => {
    const isTabOrShift = (event: KeyboardEvent | MouseEvent) =>
      event &&
      event.type === 'keydown' &&
      ((event as KeyboardEvent).key === 'Tab' || (event as KeyboardEvent).key === 'Shift');

    return (event: KeyboardEvent | MouseEvent) => {
      if (isTabOrShift(event)) return;

      setIsOpen(open);
    };
  };

  const location = useLocation();
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const handleLogout = async () => {
    navigate(getRoutePath('auth.login'));

    setTimeout(() => {
      googleLogout();
      tryCloseChat();
      const queryClient = getQueryClient();
      queryClient.resetQueries();

      GASendEvent(GAEventPath.INTERACTION_LOGOUT, 'UserLogout');

      clearToken();
    }, 50);
  };

  useEffect(() => {
    // Collapse all sub items when opening
    if (isOpen) {
      setOpenedMenu(null);
    }
  }, [isOpen]);

  return (
    <>
      <IconButton
        onClick={toggleDrawer(true)}
        sx={{
          color: 'inherit',
          display: {
            md: 'none',
            xs: quickActions ? 'none' : 'inherited',
            sm: quickActions ? 'none' : 'inherited',
          },
        }}>
        <MenuIcon />
      </IconButton>
      <SwipeableDrawer anchor="left" open={isOpen} onClose={toggleDrawer(false)} onOpen={() => {}}>
        <List sx={{ width: '300px', marginTop: '60px' }}>
          {menu.map((item) => (
            <MobileMenuItem
              key={item.id}
              item={item}
              isDea={isDea}
              isOpen={openedMenu === item.id}
              setIsOpen={(newValue) => {
                if (newValue) {
                  setOpenedMenu(item.id);
                } else {
                  setOpenedMenu(null);
                }
              }}
            />
          ))}
          <MenuItem onClick={handleLogout}>
            <ListItemText primary="Logout"></ListItemText>
          </MenuItem>
        </List>
      </SwipeableDrawer>
    </>
  );
}

function MobileMenuItem({
  basePath = '',
  item,
  isOpen,
  setIsOpen,
  isDea,
}: {
  basePath?: string;
  item: MenuItemDef;
  isOpen: boolean;
  setIsOpen: (newValue: boolean) => void;
  isDea: boolean;
}) {
  const [openedMenu, setOpenedMenu] = useState<string | null>(null);
  const isTopLevel = !basePath;
  const { memberId } = useGetIds();
  const level = basePath.split('/').length;
  const navigate = useNavigate();
  const openCreateInvoiceModal = useModalTemplateStore((state) => state.open);
  const openTakeAPayment = useTakePaymentStore((state) => state.open);
  const setContactId = useTakePaymentStore((state) => state.setContactId);

  const handleMenuItemClick = (item: MenuItemDef) => {
    if (item.subItems) {
      setIsOpen(!isOpen);
    } else if (item.link) {
      navigate(basePath + item.link);
    } else if (item.onClick) {
      if (isDea) {
        item.onClick(() => openCreateInvoiceModal('createInvoice'));
      } else {
        setContactId(memberId);
        item.onClick(() => openTakeAPayment());
      }
    }
  };

  useEffect(() => {
    // Collapse all sub items when opening
    if (isOpen) {
      setOpenedMenu(null);
    }
  }, [isOpen]);

  return (
    <>
      <ListItemButton
        onClick={() => handleMenuItemClick(item)}
        sx={{ pl: level * 2, backgroundColor: isOpen ? 'rgba(0,0,0, 0.04) !important' : 'inherit' }}>
        {!isTopLevel && (
          <ListItemIcon sx={{ minWidth: '2.5rem' }}>
            {item.icon ?? (
              <Box
                sx={{
                  width: '0.5rem',
                  height: '0.5rem',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0,0,0,0.54)',
                  ml: 1,
                }}
              />
            )}
          </ListItemIcon>
        )}
        <ListItemText primary={item.title} />
        {item.subItems && (isOpen ? <ExpandMore /> : <ChevronRight />)}
      </ListItemButton>
      {item.subItems && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List component="nav" sx={{ width: '100%' }}>
            {item.subItems.map((subItem) => (
              <MobileMenuItem
                key={subItem.id}
                basePath={basePath + item.link}
                item={subItem}
                isOpen={openedMenu === subItem.id}
                isDea={isDea}
                setIsOpen={(newValue) => {
                  if (newValue === true) {
                    setOpenedMenu(subItem.id);
                  } else {
                    setOpenedMenu(null);
                  }
                }}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}
